import { useMemo, ImgHTMLAttributes, ForwardedRef, forwardRef } from 'react';

import getOptimalImageSize from '../../lib/getOptimalImageSize';

const resolution = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

export interface PictureProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'ref'> {
    media?: Image | null;
    width?: number | null;
    height?: number | null;
    size?: string | null;
    loading?: 'lazy' | null;
    className?: string | null;
    imgRef?: ForwardedRef<HTMLImageElement>;
}

function PictureBase({
    media = null,
    width = null,
    height = null,
    size = null,
    alt = null,
    loading = null,
    className = null,
    imgRef = null,
    ...props
}: PictureProps) {
    const { sizes = null, description = null } = media || {};
    const {
        url: imageUrl,
        width: imageWidth,
        height: imageHeight,
    } = useMemo(() => {
        const imageSize =
            size !== null
                ? (sizes || []).find(
                      ({ id }) => id === size || (id === 'large' && size === 'responsive'),
                  ) || null
                : null;
        if (imageSize !== null) {
            return imageSize;
        }
        return getOptimalImageSize(media, width, height, {
            resolution,
        });
    }, [media, sizes, size, width, height]);

    const sortedSizes = useMemo(
        () =>
            (sizes || []).sort(({ width: widthA }, { width: widthB }) =>
                widthA > widthB ? 1 : -1,
            ),
        [sizes],
    );

    return (
        <img
            {...props}
            src={imageUrl}
            srcSet={
                size === 'responsive'
                    ? sortedSizes
                          .filter(({ id }) => id !== 'original')
                          .map(({ url: sizeUrl, width: sizeWidth }) => `${sizeUrl} ${sizeWidth}w`)
                          .join(', ')
                    : undefined
            }
            sizes={size === 'responsive' ? '100vw' : undefined}
            alt={alt || description || undefined}
            width={imageWidth}
            height={imageHeight}
            loading={loading || undefined}
            className={className || undefined}
            ref={imgRef}
        />
    );
}

export default forwardRef<HTMLImageElement, Omit<PictureProps, 'imgRef'>>(
    function Picture(props, ref) {
        return <PictureBase {...props} imgRef={ref} />;
    },
);
